import { useEffect, useState, type ReactNode } from 'react';
import {
  ActionIcon,
  Alert,
  AppShell,
  Burger,
  Button,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  IconAlertTriangle,
  IconBook,
  IconBriefcase,
  IconCalendarTime,
  IconCalendarWeek,
  IconCash,
  IconCalendarStats,
  IconChartBar,
  IconCoin,
  IconFileSearch,
  IconFileText,
  IconHelmet,
  IconInbox,
  IconKey,
  IconHistory,
  IconLayoutGrid,
  IconLayoutList,
  IconLogout,
  IconPalette,
  IconPencil,
  IconPhoneCall,
  IconPhonePlus,
  IconRuler,
  IconScissors,
  IconSettings,
  IconSpeakerphone,
  IconTool,
  IconTools,
  IconArrowsExchange,
  IconUserCheck,
  IconUserCircle,
  IconUserX,
  IconUsers,
} from '@tabler/icons-react';
import { Permission, USER_ROLE_LABELS, UserRole } from '@blagodar/shared';
import { useAuth } from '../auth/AuthContext';
import { SyncIndicator } from './SyncIndicator';
import { NotificationBell } from './NotificationBell';
import { ChangePasswordModal } from './ChangePasswordModal';
import { VersionNotice } from './VersionNotice';
import { useRememberedFlag } from './useRememberedFlag';
import { OpenPagesList, useOpenPages } from './OpenPages';
import { PageErrorBoundary } from './PageErrorBoundary';
import { setDesignChoice, useWarmDesign } from '../design/design';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  permission?: Permission;
  /**
   * Второе право, при котором пункт тоже виден: у некоторых экранов
   * хозяев двое. Пункт обязан показываться ровно тем, кому открыт
   * маршрут (правило 177), а маршрут в таких местах стоит под «или»
   */
  alsoPermission?: Permission;
  /** Третье право: «Расходка» у склада забора (правило 292) */
  orPermission?: Permission;
  /**
   * Что здесь — словами, второй строкой под пунктом. Показывается в
   * простом меню (правило 267): тому, кто открыл программу впервые,
   * «Сводка» и «Незаключённые» ничего не говорят
   */
  hint?: string;
}

/**
 * Меню разделено на группы по ПУТИ ЗАКАЗА, а не по отделам.
 *
 * Шестнадцать пунктов подряд человек не читает — он смотрит на первые
 * три и ищет глазами знакомое слово. «Производство» и «Конструкторский»
 * стояли между «Зарплатой» и «Документами», и найти их можно было,
 * только зная, что они там есть.
 *
 * Порядок групп — порядок, в котором заказ проходит компанию: звонок,
 * замер, проект, цех, монтаж, бумаги, деньги. Так пункт ищут не по
 * названию отдела (его новый человек не знает), а по тому, что сейчас
 * происходит с заказом.
 *
 * Заголовок группы показывается, только если в ней ЕСТЬ что показать:
 * у монтажника видимых пунктов три, и пустые названия отделов над ними
 * означали бы разделы, куда его не пустят.
 */
interface NavGroup {
  title: string;
  items: NavItem[];
}

export function AppLayout({ children }: { children: ReactNode }) {
  const [opened, { toggle, close }] = useDisclosure();
  /*
   * БОКОВАЯ ПАНЕЛЬ СВОРАЧИВАЕТСЯ И НА ПЛАНШЕТЕ (14.10.2026, владелец: «на
   * телефонах и планшетах мешает»). Шире 768 точек она стояла всегда: на
   * планшете это четверть экрана под меню, которое открывают раз в час.
   * Кнопка-гамбургер теперь есть на любом экране, а выбор помнится на
   * устройстве — это привычка, а не данные (правило 212). На сенсорном
   * экране панель по умолчанию свёрнута: планшет держат ради работы на
   * экране, а меню открывают одним нажатием.
   */
  const touch = useMediaQuery('(pointer: coarse)', false, { getInitialValueInEffect: false });
  const [wideNav, setWideNav] = useRememberedFlag('blagodar.navbar.open', !touch);
  const { user, roles, can, logout, weakPassword } = useAuth();
  const warm = useWarmDesign();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const groups: NavGroup[] = [
    {
      title: 'Заказы',
      items: [
        { to: '/today', label: 'Сегодня', icon: <IconLayoutList size={20} /> },
        { to: '/new-lead', label: 'Принять звонок', icon: <IconPhonePlus size={20} />, permission: Permission.DEAL_CREATE },
        { to: '/deals', label: 'Сделки', icon: <IconBriefcase size={20} />, permission: Permission.DEAL_VIEW, hint: 'все заказы: поиск по имени, телефону, адресу' },
      ],
    },
    {
      title: 'Замер',
      items: [
        /**
         * ЗАПИСЬ, ДЕНЬ И НЕДЕЛЯ — ИНСТРУМЕНТЫ ТОГО, КТО ЗАПИСЫВАЕТ.
         *
         * Стояли они под правом ВИДЕТЬ замеры, а его имеют и отдел
         * документов, и конструкторский: им доступна смета, чертёж,
         * лист замерщика — но не запись заказчика на час к человеку.
         * Владелец 17.09.2026 показал на эти три пункта: «не нужно
         * отделу документов». Лишний пункт в меню это шум, в котором
         * теряется нужное (правило 99).
         */
        // Замеры дня и записи, которые ещё никто не взял, — главному по замерщикам
        { to: '/measurements/today', label: 'Замеры сегодня', icon: <IconRuler size={20} />, permission: Permission.MEASUREMENT_OVERVIEW, hint: 'кто из замерщиков куда едет' },
        // Таблица компании «Справочник» листами как есть (правило 282) — главному по замерщикам и отделу документов
        { to: '/reference', label: 'Справочник замерщика', icon: <IconBook size={20} />, permission: Permission.REFERENCE_VIEW, hint: 'прайсы, плёнка, карнизы, памятки — как в таблице' },
        { to: '/schedule', label: 'Запись на замеры', icon: <IconCalendarTime size={20} />, permission: Permission.MEASUREMENT_SCHEDULE },
        { to: '/schedule/board', label: 'Сетка замерщиков', icon: <IconLayoutGrid size={20} />, permission: Permission.MEASUREMENT_SCHEDULE },
        { to: '/schedule/week', label: 'Неделя замерщиков', icon: <IconCalendarWeek size={20} />, permission: Permission.MEASUREMENT_SCHEDULE },
        { to: '/pool', label: 'Свободные записи', icon: <IconInbox size={20} />, permission: Permission.MEASUREMENT_COMPLETE },
        { to: '/my-measurements', label: 'Мои замеры', icon: <IconRuler size={20} />, permission: Permission.MEASUREMENT_COMPLETE },
        /**
         * Прикидка — ИНСТРУМЕНТ ТЕХ, КТО ЧЕРТИТ, а не всех подряд.
         *
         * Стояла она без права вовсе: считалось, что «сколько выйдет на
         * комнату три на четыре» спрашивают в трубку, и экран нужен
         * оператору. Владелец 17.09.2026 сказал иначе: «по сути нужен
         * только конструкторскому и замерщикам». Им и оставлен —
         * лишний пункт в меню у половины компании это шум, в котором
         * теряется нужное (правило 99 про то же).
         */
        {
          to: '/sketch',
          label: 'Прикинуть потолок',
          icon: <IconPencil size={20} />,
          permission: Permission.DESIGN_VIEW,
          alsoPermission: Permission.MEASUREMENT_COMPLETE,
        },
        // «Разметки листов» в меню нет ни у одной роли (владелец, 13.10.2026:
        // «спрячь это от всех ролей подряд»): это внутренняя работа над
        // чтением листов (правило 189), а не дело замерщика. Экран живёт по
        // прямой ссылке /sheet-labels — тому, у кого есть право
      ],
    },
    {
      title: 'Потолок',
      items: [
        { to: '/design', label: 'Конструкторский', icon: <IconTools size={20} />, permission: Permission.DESIGN_VIEW },
        { to: '/production', label: 'Производство', icon: <IconScissors size={20} />, permission: Permission.DESIGN_VIEW },
        // Заказчик передумал платить налом или безналом — пересчёт договора по второму листу бланка (правило 293)
        { to: '/payment-switch', label: 'Смена оплаты', icon: <IconArrowsExchange size={20} />, permission: Permission.DESIGN_VIEW },
      ],
    },
    {
      title: 'Монтаж',
      items: [
        { to: '/installations', label: 'Монтаж', icon: <IconTool size={20} />, permission: Permission.INSTALLATION_VIEW },
        { to: '/installers', label: 'Монтажники', icon: <IconHelmet size={20} />, permission: Permission.INSTALLATION_VIEW },
        // Расходка стоит у монтажа: бригада заполняет её перед выездом, склад
        // выдаёт по ней; заполняющий и выдающий приходят сюда с разными правами
        {
          to: '/consumables',
          label: 'Расходка',
          icon: <IconLayoutList size={20} />,
          permission: Permission.CONSUMABLES_FILL,
          // Кладовщик приходит сюда СМОТРЕТЬ: у него одно право, и то же
          // право есть у склада — второй строкой пункт не двоится
          alsoPermission: Permission.CONSUMABLES_VIEW_ALL,
          // Склад забора — отдать отмеченное основным складом (правило 292)
          orPermission: Permission.CONSUMABLES_PICKUP,
        },
      ],
    },
    {
      title: 'Бумаги и деньги',
      items: [
        { to: '/documents', label: 'Документы', icon: <IconFileText size={20} />, permission: Permission.DOCUMENT_VIEW },
        // Договор во всех базах ИП сразу и скан кнопкой (правило 288)
        { to: '/onec-contracts', label: 'Договоры в 1С', icon: <IconFileSearch size={20} />, permission: Permission.ONEC_CONTRACTS_SEARCH },
        // История договоров всех баз за все годы — найти заказчика без 1С (правило 294)
        ...(can(Permission.REPORT_PROFIT_TOTALS)
          ? []
          : [{ to: '/reports/history', label: 'История договоров', icon: <IconHistory size={20} />, permission: Permission.ONEC_CONTRACTS_SEARCH, hint: 'все договоры всех баз 1С за все годы' }]),
        { to: '/payroll', label: 'Зарплата', icon: <IconCash size={20} />, permission: Permission.PAYROLL_VIEW_OWN },
      ],
    },
    {
      /**
       * ОТЧЁТЫ — СВОЕЙ ГРУППОЙ, А НЕ КНОПКАМИ НА СТРАНИЦЕ (13.10.2026).
       * Незаключённые, «Кому позвонить» и прибыль договоров жили кнопками
       * над сводкой: главный по замерщикам шёл в «Отчёты» и искал там
       * кнопку. Владелец: «вместо того чтобы держать это в трёх кнопках,
       * выведем в боковую панель слева». Пункт виден тому, кому открыт
       * экран (правило 177).
       */
      title: 'Отчёты',
      items: [
        // Итоги месяца из 1С — тем, кто ведёт замерщиков (15.10.2026)
        { to: '/reports/month', label: 'Итоги месяца', icon: <IconCalendarStats size={20} />, permission: Permission.REPORT_PROFIT, hint: 'замеры, договоры и деньги одним экраном' },
        { to: '/reports', label: 'Сводка', icon: <IconChartBar size={20} />, permission: Permission.REPORT_VIEW, hint: 'общие цифры компании' },
        { to: '/reports/nonclosures', label: 'Незаключённые', icon: <IconUserX size={20} />, permission: Permission.REPORT_VIEW, hint: 'у кого из замерщиков сколько без договора' },
        // Откуда замеры — реклама из таблицы КЦ и договор по источнику из 1С (правило 286)
        { to: '/reports/lead-sources', label: 'Откуда замеры', icon: <IconSpeakerphone size={20} />, permission: Permission.REPORT_VIEW, hint: 'какая реклама сколько замеров и договоров дала' },
        { to: '/reports/nonclosures/calls', label: 'Кому позвонить', icon: <IconPhoneCall size={20} />, permission: Permission.NONCLOSURE_CALLS, hint: 'заказчики без договора — позвонить' },
        // Отчёт о работе главного по замерщикам — руководству (19.10.2026), а
        // самому главному по замерщикам — о себе (22.10.2026)
        can(Permission.REPORT_HEAD_WORK)
          ? { to: '/reports/head-work', label: 'Работа главного по замерщикам', icon: <IconUserCheck size={20} />, permission: Permission.REPORT_HEAD_WORK, hint: 'кого обзвонил и кто заключил после звонка' }
          : { to: '/reports/head-work', label: 'Моя работа по незаключённым', icon: <IconUserCheck size={20} />, permission: Permission.REPORT_HEAD_WORK_OWN, hint: 'кого я обзвонил и кто заключил после звонка' },
        { to: '/reports/contract-profit', label: 'Прибыль договоров', icon: <IconCoin size={20} />, permission: Permission.REPORT_PROFIT, hint: 'сколько зарабатываем на договоре' },
        // Все договоры всех баз 1С за все годы (правило 294)
        { to: '/reports/history', label: 'История договоров', icon: <IconHistory size={20} />, permission: Permission.REPORT_PROFIT_TOTALS, hint: 'все базы 1С за все годы: договоры, что продавали, «ЗП»' },
      ],
    },
    {
      title: 'Настройка',
      items: [
        { to: '/catalog', label: 'Справочники', icon: <IconSettings size={20} />, permission: Permission.CATALOG_MANAGE },
        // Отделу документов и сервису — монтажники: вход и пароли (правило 281)
        { to: '/staff', label: 'Сотрудники', icon: <IconUsers size={20} />, permission: Permission.USER_MANAGE, alsoPermission: Permission.USER_MANAGE_INSTALLERS },
      ],
    },
  ];

  /*
   * ПРОСТОЕ МЕНЮ — ГЛАВНОМУ ПО ЗАМЕРЩИКАМ (правило 267). Владелец: «очень
   * тяжёлая ситуация по восприятию, дедушка, изучающий телефон». Пунктов у
   * него шесть, и каждому — вторая строка словами, что там. «Сегодня» ему
   * не показывается: карточек рабочего стола по его правам нет ни одной, и
   * экран всегда говорил «ничего не висит, проекты утверждены» — про чужую
   * работу (правило 48). Панель «Открытые» — тоже нет: при шести пунктах она
   * повторяла меню второй раз ниже. День он начинает с «Замеров сегодня».
   *
   * «Сводки» ему тоже нет (15.10.2026): она считает сделки CRM, а их заводит
   * лист монтажей — на проде «договор у 96%» и ни одного замерщика. Его
   * итоги — «Итоги месяца», из 1С.
   */
  const plainMenu = roles.length > 0 && roles.every((r) => r === UserRole.SURVEYOR_HEAD);

  const visibleGroups = groups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (i) =>
          !(plainMenu && (i.to === '/today' || i.to === '/reports')) &&
          (!i.permission ||
            can(i.permission) ||
            (i.alsoPermission && can(i.alsoPermission)) ||
            (i.orPermission && can(i.orPermission))),
      ),
    }))
    .filter((g) => g.items.length > 0);

  // Горит ОДИН пункт — самый точный по адресу: на «Кому позвонить» не
  // загораются «Сводка» и «Незаключённые», на «Сетке замерщиков» — «Запись»
  const activeTo = visibleGroups
    .flatMap((g) => g.items.map((i) => i.to))
    .filter((to) => location.pathname === to || location.pathname.startsWith(`${to}/`))
    .sort((a, b) => b.length - a.length)[0];

  /*
   * ТАБЛЕТКА ВНИЗУ — на планшете и телефоне в новом виде (правило 295). Меню
   * сбоку на узком экране спрятано, а до него — бургер в углу; замерщик с
   * планшетом в одной руке достаёт до низа экрана, а не до верха. Три первых
   * раздела его меню и «Разделы» — всё меню целиком. Права те же: пункты
   * берутся из того же списка, что меню.
   */
  const narrow = useMediaQuery('(max-width: 1023px)', false, { getInitialValueInEffect: false });
  const mobileNav = useMediaQuery('(max-width: 47.99em)', false, { getInitialValueInEffect: false });
  const pill = warm && narrow && visibleGroups.length > 0;
  const pillItems = visibleGroups.flatMap((g) => g.items).slice(0, 3);
  useEffect(() => {
    const root = document.documentElement;
    if (pill) root.dataset.pill = '1';
    else delete root.dataset.pill;
    return () => {
      delete root.dataset.pill;
    };
  }, [pill]);
  const openMenu = () => (mobileNav ? toggle() : setWideNav(!wideNav));

  // Открытые страницы — как панель окон в 1С (12.10.2026): висят под меню,
  // пока их не закроют
  const openPages = useOpenPages(
    user?.id,
    visibleGroups.flatMap((g) => g.items),
  );

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened, desktop: !wideNav } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Меню" />
            <Burger
              opened={wideNav}
              onClick={() => setWideNav(!wideNav)}
              visibleFrom="sm"
              size="sm"
              aria-label={wideNav ? 'Свернуть меню' : 'Показать меню'}
              data-testid="navbar-toggle"
            />
            <Group gap={10} wrap="nowrap">
              {/* Знак дизайнера: линейка в чёрном кружке (правило 295) */}
              {warm && (
                <ThemeIcon radius="xl" size={34} variant="filled" color="blagodar" aria-hidden>
                  <IconRuler size={18} />
                </ThemeIcon>
              )}
              <Text fw={warm ? 600 : 700} size="lg" c={warm ? undefined : 'blagodar.8'}>
                БлагоДар
              </Text>
            </Group>
          </Group>

          <Group gap="sm" wrap="nowrap">
            <SyncIndicator />
            <NotificationBell />
            <Menu position="bottom-end" withArrow>
              <Menu.Target>
                <ActionIcon variant="subtle" size="lg" aria-label="Меню пользователя">
                  <IconUserCircle size={24} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>
                  {user?.fullName}
                  <br />
                  <Text span size="xs" c="dimmed">
                    {roles.map((r) => USER_ROLE_LABELS[r as UserRole]).join(', ')}
                  </Text>
                </Menu.Label>
                <Menu.Divider />
                {/* Свой пароль сотрудник меняет сам. Раньше его заводил
                    администратор в «Сотрудниках» — то есть он знал пароли
                    всех, а замерщик не мог сменить утёкший */}
                <Menu.Item
                  leftSection={<IconKey size={16} />}
                  onClick={() => setPasswordOpen(true)}
                >
                  Сменить пароль
                </Menu.Item>
                {/* Новый вид дизайнера — переключателем на этом устройстве (правило 295) */}
                <Menu.Item
                  leftSection={<IconPalette size={16} />}
                  onClick={() => setDesignChoice(warm ? 'classic' : 'warm')}
                  data-testid="design-toggle"
                >
                  {warm ? 'Вернуть прежний вид' : 'Включить новый вид'}
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconLogout size={16} />}
                  onClick={() => {
                    void logout().then((pending) => {
                      // Неотправленное остаётся на планшете и уедет, когда
                      // этот же человек войдёт снова. Сказать об этом надо
                      // сейчас: замерщик думает, что сдал работу, а она
                      // ещё лежит в очереди
                      if (pending > 0) {
                        notifications.show({
                          color: 'orange',
                          autoClose: 8000,
                          title: 'Не всё отправлено',
                          message: `На планшете осталось операций: ${pending}. Они уйдут, когда вы снова войдёте под своей учётной записью и появится интернет.`,
                        });
                      }
                      navigate('/login');
                    });
                  }}
                >
                  Выйти
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <ChangePasswordModal opened={passwordOpen} onClose={() => setPasswordOpen(false)} />

      <AppShell.Navbar p="xs">
        <ScrollArea>
          {visibleGroups.map((group) => (
            <div key={group.title}>
              <Text
                size={warm ? 'sm' : 'xs'}
                tt={warm ? undefined : 'uppercase'}
                fw={warm ? 400 : 700}
                c="dimmed"
                px="xs"
                pt="sm"
                pb={4}
              >
                {group.title}
              </Text>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  component={Link}
                  to={item.to}
                  label={item.label}
                  description={plainMenu ? item.hint : undefined}
                  leftSection={item.icon}
                  active={item.to === activeTo}
                  onClick={close}
                  styles={{
                    label: { fontSize: plainMenu ? 16 : 15, fontWeight: plainMenu ? 600 : undefined },
                    description: { fontSize: 13, color: 'var(--mantine-color-gray-7)', whiteSpace: 'normal' },
                  }}
                />
              ))}
            </div>
          ))}
          {!plainMenu && (
            <OpenPagesList
              pages={openPages.pages}
              currentKey={openPages.currentKey}
              onClose={openPages.close}
              onNavigate={close}
            />
          )}
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <VersionNotice />
        {/* Пароль меняет сам человек (правило 52), и до этой полосы он
            единственный, кто о слабом пароле НЕ знал: администратор видел
            список на рабочем столе и шёл сообщать голосом. Кнопка ведёт
            в то же окно, что и пункт меню, — иначе «смените пароль»
            означает «найдите, где это делается» */}
        {weakPassword && (
          <Alert
            color="red"
            radius="md"
            mb="md"
            icon={<IconAlertTriangle size={20} />}
            title="Ваш пароль известен посторонним"
          >
            <Text size="sm" mb="xs">
              Этот пароль программа раздавала демонстрационным набором, и сервер смотрит в
              интернет. Пока он не сменён, войти под вашим именем может кто угодно.
            </Text>
            <Button size="compact-sm" color="red" onClick={() => setPasswordOpen(true)}>
              Сменить пароль
            </Button>
          </Alert>
        )}
        {/* Ошибка на одном экране не должна уносить всё приложение:
            React снимает с экрана ВСЁ дерево, если никто не поймал
            исключение, и замерщик в чужой квартире видит белый лист.
            Ключ по адресу — иначе одна сломанная страница делает белыми
            и все следующие */}
        <PageErrorBoundary resetKey={location.pathname}>{children}</PageErrorBoundary>
      </AppShell.Main>
      {pill && (
        <nav className="bd-pill no-print" aria-label="Разделы" data-testid="bottom-pill">
          {pillItems.map((item) => (
            <UnstyledButton
              key={item.to}
              component={Link}
              to={item.to}
              className="bd-pill-btn"
              data-active={item.to === activeTo || undefined}
              onClick={close}
            >
              {item.icon}
              <span>{item.label}</span>
            </UnstyledButton>
          ))}
          <UnstyledButton className="bd-pill-btn" onClick={openMenu} aria-label="Все разделы">
            <IconLayoutGrid size={20} />
            <span>Разделы</span>
          </UnstyledButton>
        </nav>
      )}
    </AppShell>
  );
}
